import React, { useEffect, useCallback } from 'react';
import { MapPin } from 'lucide-react';
import LocationConsentService from '../services/LocationConsentService';

const BackgroundLocationDisclosure = ({ isOpen, onAccept, onDecline }) => {
  const handleAccept = useCallback(() => {
    LocationConsentService.accept();
    onAccept?.();
  }, [onAccept]);

  const handleDecline = useCallback(() => {
    LocationConsentService.decline();
    onDecline?.();
  }, [onDecline]);

  // Prevent background scrolling while open
  useEffect(() => {
    if (!isOpen) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = prev; };
  }, [isOpen]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[80000] flex items-center justify-center bg-black/60 p-4">
      <div className="w-full max-w-sm rounded-2xl bg-white shadow-2xl dark:bg-gray-800 overflow-hidden animate-in fade-in zoom-in-95 duration-200">
        {/* Header with icon */}
        <div className="flex flex-col items-center px-6 pt-6 pb-2">
          <div className="flex items-center justify-center w-14 h-14 rounded-full bg-emerald-100 dark:bg-emerald-900/40 mb-3">
            <MapPin className="w-7 h-7 text-emerald-600 dark:text-emerald-400" />
          </div>
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white text-center">
            Background Location Access
          </h2>
        </div>

        {/* Disclosure body — Google-compliant language */}
        <div className="px-6 py-4">
          <p className="text-sm text-gray-700 dark:text-gray-200 leading-relaxed">
            <span className="font-medium">Okio</span> collects location data to enable{' '}
            <span className="font-medium">GPS trail recording</span> and{' '}
            <span className="font-medium">geo-tagging of your nature discoveries</span>{' '}
            even when the app is closed or not in use.
          </p>
          <p className="mt-3 text-sm text-gray-600 dark:text-gray-300 leading-relaxed">
            Your GPS trail is continuously recorded during outdoor sessions so your phone
            can stay in your pocket between discovery points. The recorded trail and
            geo-tagged finds are displayed on your map.
          </p>
          <p className="mt-3 text-xs text-gray-500 dark:text-gray-400 leading-relaxed">
            You can change location permissions at any time in your device settings.
            Location data is not shared with third parties or used for advertising.
          </p>
        </div>

        {/* Action buttons */}
        <div className="px-6 pb-6 flex flex-col gap-2">
          <button
            type="button"
            onClick={handleAccept}
            className="w-full rounded-xl bg-emerald-600 px-4 py-3 text-sm font-semibold text-white hover:bg-emerald-700 active:scale-[0.98] transition-all"
          >
            Agree
          </button>
          <button
            type="button"
            onClick={handleDecline}
            className="w-full rounded-xl bg-gray-100 px-4 py-3 text-sm font-medium text-gray-700 hover:bg-gray-200 active:scale-[0.98] transition-all dark:bg-gray-700 dark:text-gray-200 dark:hover:bg-gray-600"
          >
            Not Now
          </button>
        </div>
      </div>
    </div>
  );
};

export default BackgroundLocationDisclosure;
